import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { EASE, WA_LINK, scrollToId } from "@/components/Reveal";

const MaskLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const FloatChip = ({ className, children, delay = 0, testid }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1, y: [0, -10, 0] }}
    transition={{
      opacity: { duration: 0.6, delay },
      scale: { duration: 0.6, delay, ease: EASE },
      y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: delay + 0.6 },
    }}
    className={`glass-chip absolute z-10 rounded-2xl px-4 py-3 ${className}`}
  >
    {children}
  </motion.div>
);

export default function Hero() {
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const copyY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden bg-obsidian">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 60% 45% at 70% 15%, rgba(224,90,43,0.22) 0%, rgba(11,12,16,0) 70%)" }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse 45% 35% at 10% 90%, rgba(245,158,11,0.10) 0%, rgba(11,12,16,0) 70%)" }}
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-28 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:pt-36">
        <motion.div style={{ y: copyY }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/12 bg-white/5 px-4 py-2"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-saffron opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-saffron" />
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-slate-300">
              Ranchi, Jharkhand — Est. 2010
            </span>
          </motion.div>

          <h1 className="font-display text-4xl font-semibold leading-[1.04] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            <MaskLine delay={0.15}>One company.</MaskLine>
            <MaskLine delay={0.3}>
              <span className="bg-gradient-to-r from-saffron via-flame to-gold bg-clip-text text-transparent">
                Three engines
              </span>
            </MaskLine>
            <MaskLine delay={0.45}>
              of <span className="font-serif italic font-medium text-saffron">taste.</span>
            </MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
            data-testid="hero-subcopy"
          >
            Taste Ride Hospitality runs Ranchi's flagship dine-in restaurants, a five-brand
            cloud kitchen network delivering on Zomato, Swiggy &amp; WhatsApp, and the canteens
            of nine hospitals. One supply chain. Three revenue engines. Zero dependence on a single format.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-invest"
              onClick={() => navigate("/investors")}
              className="group flex items-center gap-3 rounded-full bg-saffron px-7 py-4 font-display text-base font-semibold text-obsidian transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:bg-flame hover:text-cream hover:shadow-[0_16px_40px_rgba(224,90,43,0.4)]"
            >
              Explore the Opportunity
              <ArrowDown size={18} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <a
              data-testid="hero-cta-whatsapp"
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 rounded-full border border-white/20 px-7 py-4 font-display text-base font-semibold text-cream transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 hover:border-wa hover:bg-wa/10"
            >
              <MessageCircle size={18} className="text-wa" />
              WhatsApp Us Directly
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 flex flex-wrap gap-x-8 gap-y-3 border-t border-white/10 pt-6"
            data-testid="hero-ticker"
          >
            {["03 Models", "04 Outlets", "05 Brands", "09 Hospitals"].map((t) => (
              <span key={t} className="font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500">
                {t}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: imgY }} className="relative mx-auto max-w-md lg:max-w-none">
            <motion.div
              initial={{ opacity: 0, y: 60, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
              className="relative overflow-hidden rounded-t-[999px] rounded-b-[2rem] border border-white/10 shadow-[0_40px_120px_rgba(224,90,43,0.25)]"
            >
              <img
                src="/images/biryani.jpg"
                alt="Signature dum biryani by Taste Ride"
                className="aspect-[4/5] w-full scale-105 object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/50 via-transparent to-transparent" />
            </motion.div>

            <motion.svg
              viewBox="0 0 100 100"
              className="absolute -left-12 -top-12 hidden h-32 w-32 text-saffron sm:block"
              animate={{ rotate: 360 }}
              transition={{ duration: 26, ease: "linear", repeat: Infinity }}
              data-testid="hero-rotating-badge"
            >
              <defs>
                <path id="heroCircle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" fill="none" />
              </defs>
              <text className="fill-current font-mono text-[8px] font-semibold uppercase" style={{ letterSpacing: "2.6px" }}>
                <textPath href="#heroCircle">Taste Ride · Est. 2010 · Ranchi ·</textPath>
              </text>
            </motion.svg>

            <FloatChip testid="hero-chip-brands" className="-left-4 top-16 sm:-left-10" delay={1.1}>
              <p className="font-display text-lg font-semibold leading-none text-cream">05</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">Cloud Brands</p>
            </FloatChip>
            <FloatChip testid="hero-chip-hospitals" className="-right-3 bottom-24 sm:-right-8" delay={1.3}>
              <p className="font-display text-lg font-semibold leading-none text-cream">09</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">Hospitals Served</p>
            </FloatChip>
            <FloatChip testid="hero-chip-delivery" className="-left-2 bottom-6 sm:-left-6" delay={1.5}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300">Delivering on</p>
              <p className="mt-1 font-display text-sm font-semibold text-cream">Zomato · Swiggy · WhatsApp</p>
            </FloatChip>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("manifesto")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 lg:flex"
        aria-label="Scroll down"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-saffron to-transparent"
        />
      </motion.button>
    </section>
  );
}
